import { useState, useEffect } from 'react';
import { API_BASE } from '../utils/apiClient';
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Tabs,
  Tab,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PastGames } from '../components/PastGames';
import { CompanionEditor } from '../components/CompanionEditor';
import { useNotify } from 'react-admin';
import type { CompanionData } from '@garuda/shared';
import { Grid2DConfigForm, type Grid2DConfig } from '../components/Grid2DConfig';
import { getAvailableEntityTypes } from '../schemas';

export const Grid2DConfigCreate = () => {
  const notify = useNotify();
  const [activeTab, setActiveTab] = useState(0);

  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [entityType, setEntityType] = useState('cricket_player');
  const [category, setCategory] = useState('ipl');
  const [config, setConfig] = useState<Grid2DConfig | null>(null);
  const [slug, setSlug] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [companionData, setCompanionData] = useState<Partial<CompanionData> | null>(null);

  const entityTypes = getAvailableEntityTypes();

  // Check for pending config from intersection tester
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingGameConfig');
    if (pending) {
      try {
        const data = JSON.parse(pending);
        if (data.entityType) setEntityType(data.entityType);
        if (data.category) setCategory(data.category);
        sessionStorage.removeItem('pendingGameConfig');
      } catch {
        // Ignore parse errors
      }
    }
  }, []);

  const handleSave = async () => {
    if (!config || !config.answers) {
      notify('Please validate and compute answers first', { type: 'warning' });
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE}/admin/api/game-configs`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date,
          game_type: 'grid',
          entity_type: entityType,
          category,
          config,
          ...(slug.trim() && { slug: slug.trim() }),
          ...(seoDescription.trim() && { seo_description: seoDescription.trim() }),
          ...(companionData?.headline && { companion: companionData }),
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to save game config');
      }

      notify('2D Grid game config created successfully!', { type: 'success' });
      window.location.href = '#/game-configs';
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to save';
      notify(message, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => (window.location.href = '#/game-configs')}
        >
          Back
        </Button>
        <Typography variant="h4">Create 2D Grid Game Config</Typography>
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Create" />
        <Tab label="Past Games" />
      </Tabs>

      {activeTab === 1 && <PastGames gameType="grid" category={category} />}

      {activeTab === 0 && (<>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <TextField
                label="Date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                fullWidth
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Entity Type</InputLabel>
                <Select
                  value={entityType}
                  onChange={(e) => {
                    setEntityType(e.target.value);
                    setConfig(null);
                  }}
                  label="Entity Type"
                >
                  {entityTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Category"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                fullWidth
                size="small"
                helperText="e.g., 'ipl', 'tamil', 'hindi'"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="primary"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving || !config?.answers}
                fullWidth
              >
                {saving ? 'Saving...' : 'Save Game Config'}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* SEO Fields */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            SEO (Optional — auto-generated if empty)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Slug"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                fullWidth
                size="small"
                helperText="URL-friendly identifier (e.g., grid-ipl-runs-teams-may-10-2026)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="SEO Description"
                value={seoDescription}
                onChange={(e) => setSeoDescription(e.target.value)}
                fullWidth
                size="small"
                multiline
                minRows={2}
                helperText="Search engine description (100-300 chars)"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Box sx={{ mb: 3 }}>
        <CompanionEditor
          value={companionData}
          onChange={setCompanionData}
          gameType="grid"
          criteria={
            config?.criteria
              ? [
                  ...config.criteria.x.map((c) => ({ axis: 'x', label: c.label })),
                  ...config.criteria.y.map((c) => ({ axis: 'y', label: c.label })),
                ]
              : []
          }
        />
      </Box>

      <Grid2DConfigForm
        entityType={entityType}
        category={category}
        value={config as Grid2DConfig}
        onChange={setConfig}
      />

      </>)}
    </Box>
  );
};
